import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import categoryService from '../services/categoryService';
import { isUserAdmin } from '../utils/authUtils';

const CategoryContext = createContext();

// Fonction utilitaire pour construire le chemin complet (fullPath) d'une catégorie
function buildCategoryFullPaths(categoriesList) {
  // On crée un dictionnaire pour accès rapide par id
  const byId = Object.fromEntries(categoriesList.map(cat => [cat.id, cat]));
  return categoriesList.map(cat => {
    let path = [cat.name];
    let current = cat;
    while (current.parentId) {
      const parent = byId[current.parentId];
      if (!parent) break;
      path.unshift(parent.name);
      current = parent;
    }
    return { ...cat, fullPath: path.join(' > ') };
  });
}

function getCategoriesFetchers() {
  const isAdmin = isUserAdmin();

  return {
    getAllCategories: isAdmin
      ? categoryService.getAllCategoriesAdmin
      : categoryService.getAllCategories,
    getCategoriesList: categoryService.getCategoriesList
  };
}

async function fetchCategoryPayload() {
  const { getAllCategories, getCategoriesList } = getCategoriesFetchers();
  const [categoriesData, categoriesListData] = await Promise.all([
    getAllCategories(),
    getCategoriesList()
  ]);

  return {
    categoriesData,
    categoriesListData
  };
}

export const CategoryProvider = ({ children }) => {
  const [categories, setCategories] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);

  // Charger toutes les catégories au montage du composant
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        setLoading(true);

        const { categoriesData, categoriesListData } = await fetchCategoryPayload();
        setCategories(categoriesData);
        setCategoriesList(buildCategoryFullPaths(categoriesListData));
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des catégories');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, []);

  // Rechercher des catégories
  const searchCategories = useCallback(async (query, limit = 10, offset = 0) => {
    try {
      setLoading(true);
      const results = await categoryService.searchCategories(query, limit, offset);
      return results;
    } catch (err) {
      setError('Erreur lors de la recherche des catégories');
      console.error(err);
      return { categories: [], count: 0 };
    } finally {
      setLoading(false);
    }
  }, []);

  // Obtenir une catégorie par ID ou slug
  const getCategory = async (idOrSlug) => {
    try {
      // Vérification de sécurité pour éviter les requêtes avec undefined
      if (!idOrSlug || idOrSlug === 'undefined' || idOrSlug === 'null') {
        console.warn('getCategory called with invalid parameter:', idOrSlug);
        return null;
      }

      // Vérifier si la catégorie est déjà en cache
      const cachedCategory = categoriesList.find(
        cat => cat.id === idOrSlug || cat.slug === idOrSlug
      );
      
      if (cachedCategory && !loading) {
        setSelectedCategory(cachedCategory);
        return cachedCategory;
      }
      
      setLoading(true);
      const category = await categoryService.getCategory(idOrSlug);
      setSelectedCategory(category);
      setLoading(false);
      return category;
    } catch (err) {
      setError(`Erreur lors du chargement de la catégorie ${idOrSlug}`);
      setLoading(false);
      console.error(err);
      return null;
    }
  };

  // Créer une nouvelle catégorie
  const createCategory = async (categoryData) => {
    try {
      setLoading(true);
      const newCategory = await categoryService.createCategory(categoryData);

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      return newCategory;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Mettre à jour une catégorie
  const updateCategory = async (id, categoryData) => {
    try {
      setLoading(true);
      const updatedCategory = await categoryService.updateCategory(id, categoryData);

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      if (selectedCategory && selectedCategory.id === id) {
        setSelectedCategory(updatedCategory);
      }
      
      return updatedCategory;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Supprimer une catégorie
  const deleteCategory = async (id) => {
    try {
      setLoading(true);
      await categoryService.deleteCategory(id);

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      if (selectedCategory && selectedCategory.id === id) {
        setSelectedCategory(null);
      }
      
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Réorganiser les catégories
  const reorderCategories = async (categoriesOrder) => {
    try {
      setLoading(true);
      await categoryService.reorderCategories(categoriesOrder);

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Précharger le cache des catégories
  const preloadCache = async () => {
    try {
      setLoading(true);
      await categoryService.preloadCategoryCache();

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  // Fonction pour rafraîchir explicitement les catégories
const refreshCategories = async () => {
  try {
    setLoading(true);
    const { categoriesData, categoriesListData } = await fetchCategoryPayload();
    setCategories(categoriesData);
    setCategoriesList(buildCategoryFullPaths(categoriesListData));
    setError(null);
    return true;
  } catch (err) {
    setError('Erreur lors du rafraîchissement des catégories');
    console.error(err);
    throw err;
  } finally {
    setLoading(false);
  }
};

// Ajouter un écouteur d'événement pour le rafraîchissement
// Dans CategoryContext.jsx
useEffect(() => {
  const handleRefresh = () => {
    const fetchCategories = async () => {
      try {
        setLoading(true);
        const { categoriesData, categoriesListData } = await fetchCategoryPayload();
        setCategories(categoriesData);
        setCategoriesList(buildCategoryFullPaths(categoriesListData));
        setError(null);
      } catch (err) {
        setError('Erreur lors du chargement des catégories');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  };
  
  window.addEventListener('refreshCategories', handleRefresh);
  
  return () => {
    window.removeEventListener('refreshCategories', handleRefresh);
  };
}, []);

  // Vider le cache des catégories
  const clearCache = async () => {
    try {
      setLoading(true);
      await categoryService.clearCategoryCache();

      const { categoriesData, categoriesListData } = await fetchCategoryPayload();
      setCategories(categoriesData);
      setCategoriesList(buildCategoryFullPaths(categoriesListData));
      
      return true;
    } catch (err) {
      setError('Erreur lors du vidage du cache');
      console.error(err);
      throw err;
    } finally {
      setLoading(false);
    }
  };

  return (
    <CategoryContext.Provider
      value={{
        categories,
        categoriesList,
        loading,
        error,
        selectedCategory,
        searchCategories,
        getCategory,
        createCategory,
        updateCategory,
        deleteCategory,
        reorderCategories,
        preloadCache,
        clearCache,
        setSelectedCategory,
        setSelectedCategory,
      }}
    >
      {children}
    </CategoryContext.Provider>
  );
};

// Hook personnalisé pour utiliser le contexte
export const useCategories = () => {
  const context = useContext(CategoryContext);
  if (!context) {
    throw new Error('useCategories must be used within a CategoryProvider');
  }
  return context;
};